import React, { useCallback, useState, useEffect } from "react";
import { useSortable, UseSortableArguments } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RichTextEditor } from "@/components/RichTextEditor/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SortableBlockProps } from "./types";
import { VideoEmbed } from "@/modules/hub/features/ResourceLibrary/components/VideoEmbed/VideoEmbed";
import FilePickerDialog from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import {
  ResourceReferenceEditor,
  ResourceEntity,
} from "@/modules/hub/features/ResourceManagement/components/ResourceReferenceEditor/ResourceReferenceEditor";
import { FaqEditor } from "@/modules/hub/features/ResourceManagement/components/FaqEditor";
import { ColumnsEditor } from "@/modules/hub/features/ResourceManagement/components/ColumnsEditor";
import { QuoteEditor } from "@/modules/hub/features/ResourceManagement/components/QuoteEditor";
import { HeadingEditor } from "@/modules/hub/features/ResourceManagement/components/HeadingEditor";
import { TradeSelector } from "@/modules/hub/features/ResourceManagement/components/TradeSelector";
import { useNotification } from "@/context/NotificationContext";
import {
  Upload,
  RefreshCw,
  Trash2,
  Image as ImageIcon,
  File,
  FileText,
  Video,
  Music,
  Package,
  Download,
  ExternalLink,
} from "lucide-react";

export function SortableBlock({
  id,
  type,
  content,
  title,
  shortDescription,
  onRemove,
  onChange,
  fileUuid,
  file,
  resourceReferenceIds,
  referencedResources,
  tradeIds,
}: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id as UseSortableArguments["id"] });

  const { showNotification } = useNotification();

  // Format block type name for display
  const formatBlockTypeName = (blockType: string): string => {
    const typeMap: { [key: string]: string } = {
      resource_reference: "Resource References",
      faq: "FAQ",
      text: "Text",
      image: "Image",
      file: "File",
      link: "Link",
      video: "Video",
      audio: "Audio",
      columns: "Columns",
      quote: "Quote",
      heading: "Heading",
    };
    return (
      typeMap[blockType] ||
      blockType.charAt(0).toUpperCase() + blockType.slice(1)
    );
  };

  // File picker dialog states
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);

  // Track display URLs and filenames for UI only
  const [imageDisplayUrl, setImageDisplayUrl] = useState<string>("");
  const [fileDisplayUrl, setFileDisplayUrl] = useState<string>("");
  const [imageFileName, setImageFileName] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  // Initialize display data from file prop when component mounts or file changes
  useEffect(() => {
    if (type === "image" && file?.url) {
      setImageDisplayUrl(file.url);
      setImageFileName(file.name || title || "Image");
    } else if (type === "file" && file?.url) {
      setFileDisplayUrl(file.url);
      setFileName(file.name || title || "File");
    }
  }, [type, file, title]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get file icon based on file extension or title
  const getFileIcon = () => {
    const iconProps = { size: 20, className: "text-gray-500" };
    const filename = title || fileName || file?.name || "";
    const ext = filename.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "webp":
        return <ImageIcon {...iconProps} className="text-blue-500" />;
      case "mp4":
      case "avi":
      case "mov":
      case "mkv":
        return <Video {...iconProps} className="text-purple-500" />;
      case "mp3":
      case "wav":
      case "ogg":
      case "m4a":
        return <Music {...iconProps} className="text-pink-500" />;
      case "pdf":
        return <FileText {...iconProps} className="text-red-500" />;
      case "doc":
      case "docx":
      case "txt":
        return <FileText {...iconProps} className="text-blue-600" />;
      case "zip":
      case "rar":
      case "7z":
      case "tar":
        return <Package {...iconProps} className="text-yellow-600" />;
      default:
        return <File {...iconProps} />;
    }
  };

  // Handle image selection from file picker
  const handleImageSelected = useCallback(
    async (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      const selectedFile = files[0];

      // Update the block with the file UUID and clear the old file object
      onChange({
        content: "", // Keep content empty for file-based blocks
        fileUuid: selectedFile.fileUuid,
        file: null, // Clear the old file object to prevent it from overwriting the new display
      });

      // Store display URL and filename for UI only
      setImageDisplayUrl(selectedFile.publicUrl);
      setImageFileName(selectedFile.name);
      setIsImagePickerOpen(false);
    },
    [onChange],
  );

  // Handle file selection from file picker
  const handleFileSelected = useCallback(
    async (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      const selectedFile = files[0];

      // Update the block with the file UUID and clear the old file object
      onChange({
        content: "", // Keep content empty for file-based blocks
        fileUuid: selectedFile.fileUuid,
        file: null, // Clear the old file object to prevent it from overwriting the new display
        // If there's no title yet, use the filename
        title: title || selectedFile.name,
      });

      // Store display URL and filename for UI only
      setFileDisplayUrl(selectedFile.publicUrl);
      setFileName(selectedFile.name);
      setIsFilePickerOpen(false);
    },
    [onChange, title],
  );

  const handleRemoveImage = useCallback(() => {
    onChange({
      content: "",
      fileUuid: null,
    });
    setImageDisplayUrl("");
    setImageFileName("");
  }, [onChange]);

  const handleRemoveFile = useCallback(() => {
    onChange({
      content: "",
      fileUuid: null,
    });
    setFileDisplayUrl("");
    setFileName("");
  }, [onChange]);

  const handleRichTextChange = useCallback(
    (newContent: string) => {
      onChange({ content: newContent });
    },
    [onChange],
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ title: e.target.value });
    },
    [onChange],
  );

  const handleShortDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ shortDescription: e.target.value });
    },
    [onChange],
  );

  const renderTitleAndDescriptionFields = () => (
    <div className="space-y-3 mb-4">
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          Title (optional)
        </label>
        <Input
          onChange={handleTitleChange}
          placeholder="Enter block title..."
          value={title || ""}
        />
      </div>
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          Short Description (optional)
        </label>
        <Input
          onChange={handleShortDescriptionChange}
          placeholder="Enter short description..."
          value={shortDescription || ""}
        />
      </div>
    </div>
  );

  const renderBlockContent = () => {
    switch (type) {
      case "text":
        return (
          <div className="mt-2">
            <RichTextEditor
              id={id || ""}
              initialContent={content}
              onChange={handleRichTextChange}
              onRemove={onRemove}
            />
          </div>
        );

      case "image":
        const hasImage = fileUuid || (file && imageDisplayUrl);
        return (
          <div className="mt-4 space-y-4">
            {renderTitleAndDescriptionFields()}

            {hasImage ? (
              <div className="relative group max-w-full">
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                  <div className="relative">
                    <img
                      alt={title || "Content block image"}
                      className="w-full h-auto max-h-96 object-contain bg-gray-50 dark:bg-slate-900"
                      src={imageDisplayUrl}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200" />
                  </div>
                  <div className="p-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <ImageIcon className="text-blue-500" size={16} />
                        <span className="truncate max-w-[300px]">
                          {imageFileName || title || "Block image"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setIsImagePickerOpen(true)}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          <RefreshCw className="mr-1" size={14} />
                          Replace
                        </Button>
                        <Button
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={handleRemoveImage}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          <Trash2 className="mr-1 text-red-500" size={14} />
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <button
                className="w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer group"
                onClick={() => setIsImagePickerOpen(true)}
                type="button"
              >
                <Upload className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 transition-colors mb-3" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Click to select image
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Browse from File Manager
                </p>
              </button>
            )}
          </div>
        );

      case "file":
        const hasFile = fileUuid || (file && fileDisplayUrl);
        return (
          <div className="mt-4 space-y-4">
            {renderTitleAndDescriptionFields()}

            {hasFile ? (
              <div className="relative group bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-slate-600 transition-all">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">{getFileIcon()}</div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium text-gray-900 dark:text-white truncate"
                      title={title || fileName}
                    >
                      {title || fileName || "File"}
                    </p>
                    {shortDescription && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {shortDescription}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <a
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 transition-colors inline-flex items-center gap-1"
                        href={fileDisplayUrl}
                        onClick={(e) => e.stopPropagation()}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <ExternalLink size={12} />
                        View
                      </a>
                      <span className="text-gray-300 dark:text-slate-600">
                        •
                      </span>
                      <a
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 transition-colors inline-flex items-center gap-1"
                        download
                        href={fileDisplayUrl}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download size={12} />
                        Download
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      onClick={() => setIsFilePickerOpen(true)}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <RefreshCw size={14} />
                    </Button>
                    <Button
                      onClick={handleRemoveFile}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="text-red-500" size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                className="w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer group"
                onClick={() => setIsFilePickerOpen(true)}
                type="button"
              >
                <Upload className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 transition-colors mb-3" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Click to select file
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Browse from File Manager
                </p>
              </button>
            )}
          </div>
        );

      case "link":
        return (
          <div className="mt-4 space-y-4">
            {renderTitleAndDescriptionFields()}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Link URL:
              </label>
              <Input
                onChange={(e) => onChange({ content: e.target.value })}
                placeholder="https://example.com"
                value={content}
              />
            </div>

            {content && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <ExternalLink
                  className="text-gray-500 dark:text-gray-400"
                  size={16}
                />
                <a
                  className="text-blue-600 hover:underline text-sm truncate"
                  href={content}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {content}
                </a>
              </div>
            )}
          </div>
        );

      case "video":
        return (
          <div className="mt-4 space-y-4">
            {renderTitleAndDescriptionFields()}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Video URL:
              </label>
              <Input
                onChange={(e) => onChange({ content: e.target.value })}
                placeholder="YouTube, Vimeo, or direct video URL"
                value={content}
              />
            </div>

            {content ? (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                <VideoEmbed contentUrl={content.trim()} />
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Paste any video URL (YouTube, Vimeo, MP4, etc.)
              </p>
            )}
          </div>
        );

      case "audio":
        return (
          <div className="mt-4 space-y-4">
            {renderTitleAndDescriptionFields()}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Audio URL:
              </label>
              <Input
                onChange={(e) => onChange({ content: e.target.value })}
                placeholder="Audio file URL or streaming link"
                type="url"
                value={content}
              />
            </div>

            {content ? (
              <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4">
                {content.includes("transistor.fm") ? (
                  <iframe
                    className="rounded"
                    frameBorder="0"
                    height="180"
                    scrolling="no"
                    seamless
                    src={content.trim()}
                    width="100%"
                  />
                ) : content.includes("spotify.com") ? (
                  <iframe
                    allow="encrypted-media"
                    className="rounded"
                    frameBorder="0"
                    height="232"
                    src={content.trim()}
                    width="100%"
                  />
                ) : (
                  <audio className="w-full" controls>
                    <source src={content.trim()} />
                    Your browser does not support the audio element.
                  </audio>
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Paste an audio URL (MP3, Transistor, Spotify, etc.)
              </p>
            )}
          </div>
        );

      case "resource_reference":
        return (
          <div className="mt-4 space-y-4">
            {renderTitleAndDescriptionFields()}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Referenced Resources:
              </label>
              <ResourceReferenceEditor
                onChange={(references) => {
                  // Update both the IDs for submission and the full objects for display
                  onChange({
                    resourceReferenceIds: references.map((r) => r.id),
                    referencedResources: references,
                  });
                }}
                value={
                  referencedResources?.map((r) => ({
                    id: r.id,
                    name: r.name,
                    thumbnailUrl: r.thumbnailUrl,
                    primaryIcon: r.primaryIcon,
                    resourceType: r.resourceType,
                  })) || []
                }
              />
            </div>
          </div>
        );

      case "faq":
        return (
          <div className="mt-2">
            <FaqEditor
              onChange={(value) => onChange({ content: value })}
              value={content}
            />
          </div>
        );

      case "columns":
        return (
          <div className="mt-2">
            <ColumnsEditor
              onChange={(value) => onChange({ content: value })}
              value={content}
            />
          </div>
        );

      case "quote":
        return (
          <div className="mt-2">
            <QuoteEditor
              onChange={(value) => onChange({ content: value })}
              value={content}
            />
          </div>
        );

      case "heading":
        return (
          <div className="mt-2">
            <HeadingEditor
              onChange={(value) => onChange({ content: value })}
              value={content}
            />
          </div>
        );

      default:
        return <p>Unknown block type: {type}</p>;
    }
  };

  return (
    <>
      <div
        className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800 mb-6"
        ref={setNodeRef}
        style={style}
        {...attributes}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <button
              className="cursor-grab hover:bg-gray-100 dark:hover:bg-slate-700 p-1 rounded"
              type="button"
              {...listeners}
              aria-label="Drag block"
            >
              ⋮⋮
            </button>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatBlockTypeName(type)} Block
            </span>
          </div>
          <Button
            className="text-red-500 hover:text-red-700"
            onClick={onRemove}
            size="sm"
            type="button"
            variant="ghost"
          >
            Remove
          </Button>
        </div>

        {renderBlockContent()}

        {/* Trade Selection */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
          <TradeSelector
            label="Restrict to Trades"
            onChange={(ids) => onChange({ tradeIds: ids })}
            value={tradeIds || []}
          />
        </div>
      </div>

      {/* File Picker Dialog for Images */}
      <FilePickerDialog
        allowedFileTypes={[
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
          "image/svg+xml",
        ]}
        isOpen={isImagePickerOpen}
        multiSelect={false}
        onClose={() => setIsImagePickerOpen(false)}
        onSelect={handleImageSelected}
        title="Select Image"
      />

      {/* File Picker Dialog for Files */}
      <FilePickerDialog
        isOpen={isFilePickerOpen}
        multiSelect={false}
        onClose={() => setIsFilePickerOpen(false)}
        onSelect={handleFileSelected}
        title="Select File"
      />
    </>
  );
}
